import dayjs, { type Dayjs } from 'dayjs';

import { parseIdpApiDate } from 'pages/Shoppers/utils/idpApiDate';

/**
 * Days of Reveal shoppers the Shoppers page lists and charts (today − 29 … today), for both the
 * list and the timeline calls. The timeline endpoint allows up to 90 days.
 */
export const REVEAL_LIST_DAYS = 30;

/** `revealCount` never looks back further than this many days. */
export const REVEAL_COUNT_MAX_DAYS = 30;

/**
 * The seen date the Reveal dots compare against: the later of the API's stored `revealSeenDate`
 * (VMS1-6492, shared across devices) and the one this browser last sent (localStorage, covers the
 * window before a refetch and a BE that does not send the field yet). Invalid values are ignored.
 */
export function getEffectiveRevealSeenDate(
  apiSeenDate: string | null | undefined,
  localSeenDate: string | null | undefined,
): string | null {
  const valid = [apiSeenDate, localSeenDate].filter(
    (date): date is string => !!date && !!parseIdpApiDate(date),
  );
  if (valid.length === 0) return null;
  // yyyyMMdd strings compare chronologically.
  return valid.reduce((latest, date) => (date > latest ? date : latest));
}

interface IsRevealShopperUnreadOptions {
  /** `revealCount` from GET /idp/notifications; 0 or undefined means nothing is new. */
  revealCount: number | undefined;
  /** Effective yyyyMMdd seen date — see `getEffectiveRevealSeenDate`. */
  revealSeenDate: string | null;
  today?: Dayjs;
}

/**
 * Reveal shoppers have no per-shopper notification id — only a "seen up to and including this day"
 * marker — so a row counts as unread when it returned after the seen date, or within the API's
 * 30-day count window when no seen date is known. Nothing is unread while
 * the API reports no new sessions, so another device's "mark all as seen" clears the dots too.
 */
export function isRevealShopperUnread(
  returnedDate: string | undefined,
  { revealCount, revealSeenDate, today = dayjs() }: IsRevealShopperUnreadOptions,
): boolean {
  if (!revealCount) return false;

  const raw = returnedDate?.trim();
  if (!raw) return false;
  const returned = dayjs(raw);
  if (!returned.isValid()) return false;

  const seenDate = parseIdpApiDate(revealSeenDate);
  if (seenDate) return returned.isAfter(seenDate, 'day');

  const windowStart = today.startOf('day').subtract(REVEAL_COUNT_MAX_DAYS - 1, 'day');
  return !returned.isBefore(windowStart, 'day');
}

/**
 * Count shown in a Shoppers notification badge (side nav and the Reveal / Revive toggle). Capped at
 * 999 rather than "999+" so the value fits the round badge's three-digit pill; undefined hides it.
 */
export function formatIdpNotificationCount(count: number): number | undefined {
  if (count <= 0) return undefined;
  return Math.min(count, 999);
}
